/**
 * Birthdays we only know the month and day for (F-FE-N32).
 *
 * Mirrors `backend/internal/customer/birthday.go` — the two must agree on the
 * placeholder year and on the rule that the year is never displayed.
 */

/**
 * Stands in for "we don't know the year".
 *
 * It MUST be a leap year, or 29 February is not a valid date and Postgres
 * rejects the write outright — a failure that hits one birthday in 365. Note
 * 1900 is NOT a leap year (divisible by 100, not by 400), so the most
 * intuitive-looking placeholder is the broken one. 2000 is a leap year but is a
 * plausible real birth year, and a placeholder that collides with real data can
 * never be told apart from it afterwards.
 */
export const BIRTHDAY_PLACEHOLDER_YEAR = 1904

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Month options for a picker: value is the calendar month number, 1-12. */
export const MONTH_OPTIONS = MONTH_NAMES.map((name, i) => ({ value: i + 1, label: name }))

/**
 * Every month offers 31 days. Trimming per selected month would silently drop a
 * day the user had already picked when they changed the month; the parser
 * rejects an impossible pair anyway, with a message.
 */
export const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

/** Split a stored `YYYY-MM-DD` into picker values. Blank for no birthday. */
export function splitMonthDay(iso: string | null | undefined): {
  month: number | ''
  day: number | ''
} {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? '').trim())
  if (!m) return { month: '', day: '' }
  return { month: Number(m[2]), day: Number(m[3]) }
}

/**
 * Build the value to store from picker selections.
 *
 * Returns null for "no birthday" — both blank, only one filled, or a pair that
 * is not a real calendar day. A half-filled birthday is refused rather than
 * half-stored: it cannot be acted on by any campaign, but it looks like real
 * data to whoever reads it next.
 */
export function toMonthDayISO(month: number | '', day: number | ''): string | null {
  if (month === '' || day === '') return null
  if (!Number.isInteger(month) || !Number.isInteger(day)) return null
  if (month < 1 || month > 12 || day < 1 || day > 31) return null

  // Date normalises overflow rather than failing — 30 February becomes
  // 1 March — so comparing the result back to the input is a complete validity
  // check for every month, with no per-month day table to keep in sync. It
  // accepts 29 February precisely because the placeholder year is a leap year.
  const d = new Date(Date.UTC(BIRTHDAY_PLACEHOLDER_YEAR, month - 1, day))
  if (d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null

  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${BIRTHDAY_PLACEHOLDER_YEAR}-${mm}-${dd}`
}

/**
 * Render a birthday as "14 March" — never the year.
 *
 * Applies to EVERY birthday, not only placeholder ones. Counter-captured dates
 * carry a real year, and showing it for those but not these would advertise
 * exactly the distinction this design keeps out of the interface. A placeholder
 * that never reaches the screen cannot be misread as real data, which is what
 * makes storing one honest rather than a shortcut.
 */
export function formatMonthDay(iso: string | null | undefined): string {
  const { month, day } = splitMonthDay(iso)
  if (month === '' || day === '') return ''
  if (month < 1 || month > 12) return ''
  return `${day} ${MONTH_NAMES[month - 1]}`
}

/**
 * Whether a stored birthday carries a real year.
 *
 * Nothing renders this today — the year is never shown and no feature uses age.
 * It exists so the distinction is recoverable in one call if age-based
 * segmentation is ever wanted, rather than by rediscovering the convention.
 */
export function hasRealYear(iso: string | null | undefined): boolean {
  const m = /^(\d{4})-\d{2}-\d{2}/.exec(String(iso ?? '').trim())
  return m != null && Number(m[1]) !== BIRTHDAY_PLACEHOLDER_YEAR
}
